import React, { useEffect, useState } from 'react';
import { Modal, Icon, Segment, Label, Loader, Dimmer, Message, Menu } from 'semantic-ui-react';
import {
  ContractAuditLogDto,
  ContractLineAuditLogDto,
  contractService,
} from '../../services/contracts/ContractService';
import { LineAuditTimeline, formatLineAuditDateTime, renderLineFieldChange } from './LineAuditTimeline';

interface EditHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  contractId: string;
}

const FIELD_LABELS: Record<string, string> = {
  TipoContratacao: 'Tipo de Contratação',
  DiaVencimento: 'Dia de Vencimento',
  NumeroP2B: 'Número P2B',
  ComprometimentoMinimo: 'Comprometimento Mínimo',
  Token: 'Token',
  Fastchip: 'Fastchip',
  ValorTotalMensal: 'Valor Total Mensal',
  TipoFatura: 'Tipo de Fatura',
  EnvioFatura: 'Envio de Fatura',
  OrigemVenda: 'Origem da Venda',
  Endereco: 'Endereço de Entrega',
  Administrador: 'Responsável pela Auditoria',
  Representantes: 'Representantes Legais',
  Recebedores: 'Recebedores Autorizados',
  Testemunhas: 'Testemunhas',
  Linhas: 'Linhas do Contrato',
};

const EditHistoryModal: React.FC<EditHistoryModalProps> = ({ isOpen, onClose, contractId }) => {
  const [activeTab, setActiveTab] = useState<'contract' | 'lines'>('contract');
  const [contractLogs, setContractLogs] = useState<ContractAuditLogDto[]>([]);
  const [lineLogs, setLineLogs] = useState<ContractLineAuditLogDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setContractLogs([]);
      setLineLogs([]);
      setError(null);
      setLoading(false);
      return;
    }

    if (!contractId) return;

    let cancelled = false;

    setLoading(true);
    setError(null);
    setActiveTab('contract');

    Promise.allSettled([
      contractService.getContractAuditLog(contractId),
      contractService.getContractLineAuditLog(contractId),
    ])
      .then(([contractResult, lineResult]) => {
        if (cancelled) return;

        if (contractResult.status === 'rejected') {
          console.error('Erro ao carregar histórico de contrato:', contractResult.reason);
          setContractLogs([]);
          setError('Erro ao carregar histórico de edições do contrato');
        } else {
          setContractLogs(contractResult.value);
        }

        if (lineResult.status === 'rejected') {
          console.error('Erro ao carregar histórico de linhas:', lineResult.reason);
          setLineLogs([]);
          if (contractResult.status === 'fulfilled') {
            setError(null);
          }
        } else {
          setLineLogs(lineResult.value);
        }

        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, contractId]);

  const totalCount = activeTab === 'contract' ? contractLogs.length : lineLogs.length;

  return (
    <Modal open={isOpen} onClose={onClose} size="large" closeIcon>
      <Modal.Header>
        <Icon name="history" />
        Histórico de Edições do Contrato
      </Modal.Header>
      <Modal.Content scrolling style={{ maxHeight: '70vh' }}>
        <Menu pointing secondary style={{ marginBottom: '16px' }}>
          <Menu.Item
            name="contract"
            active={activeTab === 'contract'}
            onClick={() => setActiveTab('contract')}
          >
            <Icon name="file alternate outline" />
            Contrato ({contractLogs.length})
          </Menu.Item>
          <Menu.Item
            name="lines"
            active={activeTab === 'lines'}
            onClick={() => setActiveTab('lines')}
          >
            <Icon name="mobile alternate" />
            Linhas ({lineLogs.length})
          </Menu.Item>
        </Menu>

        {loading && (
          <Dimmer active inverted>
            <Loader>Carregando histórico...</Loader>
          </Dimmer>
        )}

        {error && <Message negative>{error}</Message>}

        {!loading && !error && activeTab === 'contract' && contractLogs.length === 0 && (
          <Message info>
            <Icon name="info circle" />
            Nenhuma edição registrada para este contrato.
          </Message>
        )}

        {!loading &&
          activeTab === 'contract' &&
          contractLogs.map((log, index) => {
            let oldVals: Record<string, unknown> | null = null;
            let newVals: Record<string, unknown> | null = null;
            try {
              oldVals = log.oldValues ? JSON.parse(log.oldValues) : null;
            } catch {
              /* ignore */
            }
            try {
              newVals = log.newValues ? JSON.parse(log.newValues) : null;
            } catch {
              /* ignore */
            }

            return (
              <Segment key={log.id} style={{ position: 'relative', paddingLeft: '40px' }}>
                <div
                  style={{
                    position: 'absolute',
                    left: '16px',
                    top: '16px',
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: index === 0 ? '#2185d0' : '#ccc',
                  }}
                />
                {index < contractLogs.length - 1 && (
                  <div
                    style={{
                      position: 'absolute',
                      left: '19px',
                      top: '28px',
                      width: '2px',
                      height: 'calc(100% - 28px)',
                      backgroundColor: '#e0e0e0',
                    }}
                  />
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <div>
                    <Label size="small" color={index === 0 ? 'blue' : 'grey'}>
                      <Icon name="edit" />
                      {log.action === 'Update' ? 'Edição' : log.action === 'Create' ? 'Criação' : log.action}
                    </Label>
                    <span style={{ marginLeft: '8px', fontSize: '0.9em', color: '#666' }}>
                      {formatLineAuditDateTime(log.timestamp)}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.85em', color: '#888' }}>
                    <Icon name="user" />
                    {log.userName || 'Desconhecido'}
                    {log.userEmail && <span style={{ marginLeft: '4px' }}>({log.userEmail})</span>}
                  </div>
                </div>

                {log.changedFields.length > 0 ? (
                  <div style={{ marginTop: '8px' }}>
                    <div style={{ fontSize: '0.85em', color: '#888', marginBottom: '6px' }}>
                      {log.changedFields.length} campo(s) alterado(s):
                    </div>
                    {log.changedFields.map((field) =>
                      renderLineFieldChange(field, oldVals, newVals, FIELD_LABELS)
                    )}
                  </div>
                ) : (
                  <div style={{ fontSize: '0.85em', color: '#888', fontStyle: 'italic' }}>
                    Nenhuma alteração de campo detectada
                  </div>
                )}
              </Segment>
            );
          })}

        {!loading && !error && activeTab === 'lines' && <LineAuditTimeline logs={lineLogs} />}
      </Modal.Content>
      <Modal.Actions>
        <Label size="small" style={{ float: 'left', marginTop: '8px' }}>
          <Icon name="info circle" />
          Total: {totalCount} registro(s)
        </Label>
        <button className="ui button" onClick={onClose}>
          Fechar
        </button>
      </Modal.Actions>
    </Modal>
  );
};

export default EditHistoryModal;
